import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { clinicIntegrationEventsService, authService } from '../services/api';
import { ReactComponent as RefreshIcon } from '../assets/refresh.svg';

const ClinicIntegrationEvents = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  // Filter states
  const [eventType, setEventType] = useState(searchParams.get('event_type') || '');
  const [source, setSource] = useState(searchParams.get('source') || '');
  const [objectId, setObjectId] = useState(searchParams.get('object_id') || '');
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [createdBy, setCreatedBy] = useState(searchParams.get('created_by') || '');

  // Pagination
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const limit = 50;

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);

      const skip = (currentPage - 1) * limit;
      const response = await clinicIntegrationEventsService.listEvents(
        skip,
        limit,
        eventType || null,
        source || null,
        objectId || null,
        search || null,
        createdBy || null
      );

      if (response && response.items) {
        setEvents(response.items);
        setTotal(response.total || 0);
      } else {
        setEvents([]);
        setTotal(0);
      }
    } catch (err) {
      console.error('Error fetching clinic integration events:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to fetch events');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [currentPage, eventType, source, objectId, search, createdBy]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchEvents();
  };

  const handleFilterChange = (filterName, value) => {
    setCurrentPage(1);
    if (filterName === 'event_type') setEventType(value);
    if (filterName === 'source') setSource(value);
    if (filterName === 'object_id') setObjectId(value);
    if (filterName === 'search') setSearch(value);
    if (filterName === 'created_by') setCreatedBy(value);

    // Update URL params
    const newParams = new URLSearchParams();
    if (value) newParams.set(filterName, value);
    setSearchParams(newParams);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', page.toString());
    setSearchParams(newParams);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatJSON = (obj) => {
    if (!obj) return 'N/A';
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Clinic Integration Events</h1>
            <p className="text-gray-600 mt-1">
              View and filter clinic integration event logs
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center px-4 py-2 bg-[#075e54] text-white rounded-lg hover:bg-[#064e45] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <RefreshIcon className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Event Type
              </label>
              <input
                type="text"
                value={eventType}
                onChange={(e) => handleFilterChange('event_type', e.target.value)}
                placeholder="Filter by event type"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#075e54]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Source
              </label>
              <input
                type="text"
                value={source}
                onChange={(e) => handleFilterChange('source', e.target.value)}
                placeholder="Filter by source"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#075e54]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Object ID
              </label>
              <input
                type="text"
                value={objectId}
                onChange={(e) => handleFilterChange('object_id', e.target.value)}
                placeholder="Filter by object ID"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#075e54]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Created By
              </label>
              <input
                type="text"
                value={createdBy}
                onChange={(e) => handleFilterChange('created_by', e.target.value)}
                placeholder="Filter by created by"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#075e54]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <input
                type="text"
                value={search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Search in event type, annotation, source"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#075e54]"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">Total Events</p>
            <p className="text-2xl font-semibold text-gray-900">{total}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Page {currentPage} of {totalPages || 1}</p>
          </div>
        </div>
      </div>

      {/* Events Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#075e54]"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading events</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No events found</h3>
          <p className="mt-1 text-sm text-gray-500">Try adjusting your filters</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Object ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created By</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Annotation</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {events.map((event) => (
                    <tr key={event.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">{event.id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{event.event_type}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <ObjectIdHover objectId={event.object_id} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{event.source || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <CreatedByCell createdBy={event.created_by} source={event.source} />
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{event.annotation || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(event.created_at)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <EventDataHover event={event} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex justify-center">
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {[...Array(totalPages)].map((_, i) => {
                  const page = i + 1;
                  if (page === 1 || page === totalPages || (page >= currentPage - 2 && page <= currentPage + 2)) {
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === page
                            ? 'z-10 bg-[#075e54] border-[#075e54] text-white'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (page === currentPage - 3 || page === currentPage + 3) {
                    return <span key={page} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">...</span>;
                  }
                  return null;
                })}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </nav>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Created By Cell Component
const CreatedByCell = ({ createdBy, source }) => {
  const [showModal, setShowModal] = useState(false);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleClick = async () => {
    if (!createdBy) return;

    setLoading(true);
    setError(null);
    try {
      // If source is "integration", fetch API key info instead of user info
      if (source === 'integration') {
        const apiKey = await clinicIntegrationEventsService.getApiKeyById(createdBy);
        setInfo(apiKey);
      } else {
        const user = await authService.getUserById(createdBy);
        setInfo(user);
      }
      setShowModal(true);
    } catch (err) {
      console.error('Error fetching info:', err);
      setError(source === 'integration' ? 'Failed to load API key information' : 'Failed to load user information');
    } finally {
      setLoading(false);
    }
  };

  if (!createdBy) {
    return <span className="text-gray-400">N/A</span>;
  }

  return (
    <>
      <button
        onClick={handleClick}
        disabled={loading}
        className="text-[#075e54] hover:text-[#064e45] hover:underline cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? 'Loading...' : createdBy}
      </button>

      {showModal && (
        <InfoModal
          info={info}
          error={error}
          source={source}
          onClose={() => {
            setShowModal(false);
            setInfo(null);
            setError(null);
          }}
        />
      )}
    </>
  );
};

// Info Modal Component (handles both User and API Key info)
const InfoModal = ({ info, error, source, onClose }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const isApiKey = source === 'integration';

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" onClick={onClose}>
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" onClick={(e) => e.stopPropagation()}>
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                {isApiKey ? 'API Key Information' : 'User Information'}
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 transition-colors"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            ) : info ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {isApiKey ? (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">API Key ID</label>
                        <p className="mt-1 text-sm text-gray-900 font-mono">{info.api_key_id || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Name</label>
                        <p className="mt-1 text-sm text-gray-900">{info.name || 'N/A'}</p>
                      </div>
                      {info.description && (
                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700">Description</label>
                          <p className="mt-1 text-sm text-gray-900">{info.description}</p>
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Clinic ID</label>
                        <p className="mt-1 text-sm text-gray-900">{info.clid || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Status</label>
                        <p className="mt-1 text-sm">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            info.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {info.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </p>
                      </div>
                      {info.permissions && Object.keys(info.permissions).length > 0 && (
                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700">Permissions</label>
                          <p className="mt-1 text-sm text-gray-900">
                            {JSON.stringify(info.permissions, null, 2)}
                          </p>
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Created At</label>
                        <p className="mt-1 text-sm text-gray-900">{formatDate(info.created_at)}</p>
                      </div>
                      {info.last_used_at && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Last Used At</label>
                          <p className="mt-1 text-sm text-gray-900">{formatDate(info.last_used_at)}</p>
                        </div>
                      )}
                      {info.expires_at && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Expires At</label>
                          <p className="mt-1 text-sm text-gray-900">{formatDate(info.expires_at)}</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">User ID</label>
                        <p className="mt-1 text-sm text-gray-900 font-mono">{info.id}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">User Type</label>
                        <p className="mt-1 text-sm text-gray-900 capitalize">{info.user_type?.replace('_', ' ') || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Username</label>
                        <p className="mt-1 text-sm text-gray-900">{info.username || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Email</label>
                        <p className="mt-1 text-sm text-gray-900">{info.email || 'N/A'}</p>
                      </div>
                      {info.clid && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Clinic ID</label>
                          <p className="mt-1 text-sm text-gray-900">{info.clid}</p>
                        </div>
                      )}
                      {info.phone_number && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                          <p className="mt-1 text-sm text-gray-900">{info.phone_number}</p>
                        </div>
                      )}
                      {info.role && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Role</label>
                          <p className="mt-1 text-sm text-gray-900 capitalize">{info.role}</p>
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Status</label>
                        <p className="mt-1 text-sm">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            info.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {info.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </p>
                      </div>
                      {info.is_temp !== undefined && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Temp User</label>
                          <p className="mt-1 text-sm text-gray-900">{info.is_temp ? 'Yes' : 'No'}</p>
                        </div>
                      )}
                      {info.is_verified !== undefined && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Verified</label>
                          <p className="mt-1 text-sm text-gray-900">{info.is_verified ? 'Yes' : 'No'}</p>
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Created At</label>
                        <p className="mt-1 text-sm text-gray-900">{formatDate(info.created_at)}</p>
                      </div>
                      {info.last_login && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Last Login</label>
                          <p className="mt-1 text-sm text-gray-900">{formatDate(info.last_login)}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#075e54] mx-auto"></div>
              </div>
            )}
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[#075e54] text-base font-medium text-white hover:bg-[#064e45] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#075e54] sm:ml-3 sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Object ID Hover Component
const ObjectIdHover = ({ objectId }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  const isVisible = isLocked || isHovered;

  const handleClick = (e) => {
    e.stopPropagation();
    setIsLocked(!isLocked);
  };

  // Calculate tooltip position to avoid overflow
  useEffect(() => {
    if (isVisible && objectId && triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
      let top = triggerRect.top - tooltipRect.height - 8;

      if (left + tooltipRect.width > viewportWidth - 16) {
        left = viewportWidth - tooltipRect.width - 16;
      }

      if (left < 16) {
        left = 16;
      }

      if (top < 16) {
        top = triggerRect.bottom + 8;
      }

      if (top + tooltipRect.height > viewportHeight - 16) {
        top = Math.max(16, (viewportHeight - tooltipRect.height) / 2);
      }

      tooltipRef.current.style.left = `${left}px`;
      tooltipRef.current.style.top = `${top}px`;
    }
  }, [isVisible, objectId]);

  // Close tooltip when clicking outside (only when locked)
  useEffect(() => {
    if (isLocked && objectId) {
      const handleClickOutside = (event) => {
        if (
          tooltipRef.current &&
          triggerRef.current &&
          !tooltipRef.current.contains(event.target) &&
          !triggerRef.current.contains(event.target)
        ) {
          setIsLocked(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isLocked, objectId]);

  // Close tooltip when pressing ESC (only when locked)
  useEffect(() => {
    if (isLocked && objectId) {
      const handleEscape = (event) => {
        if (event.key === 'Escape') {
          setIsLocked(false);
        }
      };

      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isLocked, objectId]);

  if (!objectId) {
    return <span className="text-gray-400">N/A</span>;
  }

  return (
    <>
      <div
        ref={triggerRef}
        className="flex items-center space-x-1 cursor-help relative"
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => !isLocked && setIsHovered(false)}
      >
        <span className="text-gray-400 text-xs">•••</span>
      </div>

      {/* Hover Tooltip */}
      {isVisible && (
        <div
          ref={tooltipRef}
          className="fixed z-[9999] p-3 bg-gray-900 text-white text-xs rounded-lg shadow-2xl pointer-events-auto font-mono"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => !isLocked && setIsHovered(false)}
          onClick={(e) => e.stopPropagation()}
        >
          {isLocked && (
            <button
              onClick={handleClick}
              className="absolute top-1 right-1 text-gray-400 hover:text-white transition-colors z-10"
              aria-label="Close"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          <div className="pr-6">{objectId}</div>
        </div>
      )}
    </>
  );
};

// Event Data Hover Component
const EventDataHover = ({ event }) => {
  const [isLocked, setIsLocked] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  const formatJSON = (obj) => {
    if (!obj) return 'N/A';
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  };

  const hasData = event.event_data || event.response;
  const isVisible = isLocked || isHovered;

  const handleClick = (e) => {
    e.stopPropagation();
    setIsLocked(!isLocked);
  };

  // Calculate tooltip position to avoid overflow
  useEffect(() => {
    if (isVisible && hasData && triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
      let top = triggerRect.top - tooltipRect.height - 8; // Position above by default

      // Adjust if tooltip would overflow on the right
      if (left + tooltipRect.width > viewportWidth - 16) {
        left = viewportWidth - tooltipRect.width - 16;
      }

      // Adjust if tooltip would overflow on the left
      if (left < 16) {
        left = 16;
      }

      // If not enough space above, position below
      if (top < 16) {
        top = triggerRect.bottom + 8;
      }

      // If not enough space below either, position in viewport center vertically
      if (top + tooltipRect.height > viewportHeight - 16) {
        top = Math.max(16, (viewportHeight - tooltipRect.height) / 2);
      }

      tooltipRef.current.style.left = `${left}px`;
      tooltipRef.current.style.top = `${top}px`;
    }
  }, [isVisible, hasData]);

  // Close tooltip when clicking outside (only when locked)
  useEffect(() => {
    if (isLocked && hasData) {
      const handleClickOutside = (event) => {
        if (
          tooltipRef.current &&
          triggerRef.current &&
          !tooltipRef.current.contains(event.target) &&
          !triggerRef.current.contains(event.target)
        ) {
          setIsLocked(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isLocked, hasData]);

  // Close tooltip when pressing ESC (only when locked)
  useEffect(() => {
    if (isLocked && hasData) {
      const handleEscape = (event) => {
        if (event.key === 'Escape') {
          setIsLocked(false);
        }
      };

      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isLocked, hasData]);

  if (!hasData) {
    return <span className="text-gray-400">No data</span>;
  }

  const tooltipContent = (
    <div className="space-y-4 max-h-[500px] overflow-y-auto">
      {/* Event Data Section */}
      {event.event_data && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold text-blue-300">Event Data</label>
          </div>
          <pre className="bg-gray-800 p-3 rounded border border-gray-700 overflow-auto text-xs font-mono whitespace-pre-wrap break-words">
            {formatJSON(event.event_data)}
          </pre>
        </div>
      )}

      {/* Response Section */}
      {event.response && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold text-green-300">Response</label>
          </div>
          <pre className="bg-gray-800 p-3 rounded border border-gray-700 overflow-auto text-xs font-mono whitespace-pre-wrap break-words">
            {formatJSON(event.response)}
          </pre>
        </div>
      )}
    </div>
  );

  return (
    <>
      <div
        ref={triggerRef}
        className="flex items-center space-x-2 cursor-help relative"
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => !isLocked && setIsHovered(false)}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-5 w-5 transition-colors ${isLocked ? 'text-[#064e45]' : 'text-[#075e54] hover:text-[#064e45]'}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span className={`font-medium text-xs ${isLocked ? 'text-[#064e45]' : 'text-[#075e54] hover:text-[#064e45]'}`}>
          {isLocked ? 'Click to unlock' : 'Hover to view'}
        </span>
      </div>

      {/* Hover Tooltip */}
      {isVisible && (
        <div
          ref={tooltipRef}
          className="fixed z-[9999] w-[600px] max-w-[calc(100vw-2rem)] p-4 bg-gray-900 text-white text-xs rounded-lg shadow-2xl pointer-events-auto"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => !isLocked && setIsHovered(false)}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button when locked */}
          {isLocked && (
            <button
              onClick={handleClick}
              className="absolute top-2 right-2 text-gray-400 hover:text-white transition-colors z-10"
              aria-label="Close"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}

          {tooltipContent}
        </div>
      )}
    </>
  );
};

export default ClinicIntegrationEvents;

